import classNames from 'classnames'
import { memo, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useNavigate } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IDataBuilderConfig, IGetSeriesDataBody } from '~/types/graph-builder'

import AnalyticBlockMenu from '~/components/AnalyticBlocksMenu'
import AnalyticGenericBlock from '~/components/AnalyticGenericBlock'
import AnalyticTooltip from '~/components/AnalyticsTooltip'
import AnalyticWarningTooltip from '~/components/AnalyticsWarningTooltip'
import BlockValue from '~/components/Blocks/Value'
import DropdownMenu from '~/components/DropdownMenu'
import Variation, { getDirection } from '~/components/Variation'
import { AUTHORIZATIONS, dataNotAvailableText } from '~/const'
import { analyticsColors } from '~/const/chart'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useCompareCohortsContext } from '~/providers/CompareCohorts'
import { useGetCompareCohortOverallQueries } from '~/queries/graphs-builder'
import { COHORT_TYPE } from '~/types/compare-cohorts-context'
import { getCohortBodies } from '~/utils/compare-cohorts'
import { formatGraphTitle, formatValue } from '~/utils/graphs-builder'

import './styles.scss'

export interface Props {
  apiSelectionFn?: (body: IGetSeriesDataBody) => Promise<any>
  appKey: string
  className?: string
  config: IDataBuilderConfig
  deviceType: string
  isAppLoading: boolean
  kpiField: string
  lazy?: boolean
  noMenu?: boolean
}

const DynamicDataBlockWithCompare = ({
  apiSelectionFn,
  appKey,
  className,
  config,
  deviceType,
  isAppLoading,
  kpiField,
  lazy = true,
  noMenu,
}: Props): ReactElement => {
  /** Local state */

  const userAuthorizedToEdit = useIsUserAuthorized([AUTHORIZATIONS.ANALYTICS.GRAPH_BUILDER.WRITE])

  const navigate = useNavigate()

  const { state } = useCompareCohortsContext()

  const { inView, ref } = useInView({ triggerOnce: true })

  const genericBlockClasses = classNames('dynamic-data-block-container', className)

  const title = useMemo(() => formatGraphTitle(config.title), [config.title])

  const isQueryEnabled = useMemo(() => {
    if (config.series.length > 0 && config.series.find((el) => el.values.length > 0)) {
      return lazy && config.id ? inView : true
    }
    return false
  }, [config.id, config.series, inView, lazy])

  const menu = useMemo(
    () => [
      ...(userAuthorizedToEdit
        ? [
            {
              items: [
                {
                  label: 'Edit',
                  onClick: () => navigate(`/analytics/ca/builder/${config.id}`),
                },
              ],
            },
          ]
        : []),
    ],
    [config.id, navigate, userAuthorizedToEdit]
  )

  /** Queries & Mutations */

  const bodies = [
    getCohortBodies(
      kpiField,
      config.series[0].normalizedBy,
      state[COHORT_TYPE.COHORT_1],
      config.series[0].values,
      appKey,
      deviceType
    ),
    getCohortBodies(
      kpiField,
      config.series[0].normalizedBy,
      state[COHORT_TYPE.COHORT_2],
      config.series[0].values,
      appKey,
      deviceType
    ),
  ].flat()

  const overall = useGetCompareCohortOverallQueries({
    appKey,
    bodies,
    kpiField,
    options: {
      enabled: isQueryEnabled,
    },
    queryFn: apiSelectionFn,
    state,
  })

  const isError = overall.some(({ isError }) => isError)

  const isDataLoading = overall.some(({ isPending }) => isPending)

  /** Handlers */

  const getCohortColor = (index: number) => analyticsColors[index]

  const renderChildren = () => {
    if (isError) return <div className="no-data-container">API error</div>

    if (!(overall && overall.some(({ data }) => !!data?.data))) {
      return <div className="no-data-container">{dataNotAvailableText}</div>
    }

    return (
      <div className="data-value-container">
        {overall.map(({ data }, index) => {
          const baseCohortValue = !!overall[0].data?.data ? overall[0].data?.data[0].data : 0

          const value = data?.data

          const evolution = !!baseCohortValue && !!value ? (value[0].data / baseCohortValue - 1) * 100 : 0

          return (
            <div className="data-variation-container" key={index}>
              <BlockValue className="data-wrapper">
                <span style={{ color: getCohortColor(index) }}>
                  {!!value ? formatValue(config.yAxes[0], value[0].data) : 0}
                </span>
              </BlockValue>
              {index > 0 ? (
                <Variation
                  direction={getDirection(evolution)}
                  reversed={config.wantNegativeEvolution}
                  value={evolution}
                />
              ) : null}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <>
      {/**
       * This id is used as an anchor.
       * The `useScrollToElement` in the generic `Layout` component will detect if an anchor is present
       * in the url and scroll to it if the anchor is present in the view.
       */}
      {config.id ? <div id={config.id} ref={ref} /> : null}
      <AnalyticGenericBlock
        className={genericBlockClasses}
        isDraggable={false}
        isLoading={isAppLoading || isDataLoading}
        title={title}
      >
        <AnalyticBlockMenu>
          {/* {isIntranet() && hasFavourite ? <AddToFavourites frontendFeature={frontendDataKey} /> : null} */}
          {config.warningMessage ? <AnalyticWarningTooltip message={config.warningMessage} /> : null}
          {config.tooltip?.how || config.tooltip?.what ? (
            <AnalyticTooltip value={{ ...config.tooltip, glossarySearch: config.tooltip.relatedGlossaryItems }} />
          ) : null}
          {!noMenu ? <DropdownMenu menu={menu} /> : null}
        </AnalyticBlockMenu>
        {renderChildren()}
      </AnalyticGenericBlock>
    </>
  )
}

export default memo(DynamicDataBlockWithCompare)
